import { Button, Col, Form, Input, Layout, Row, Select, Table } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import AlertNotAllow from '../AlertNotAllow';
import translate from '../../i18n/translate';
import Confirm from '../Confirm/Confirm';
import { storeContext } from '../../context';
import api from '../../helpers/api';
import { isAuthorized } from '../../helpers/verifyStatusErrorResponse';
import CustomAutoComplete from '../CustomAutoComplete';
import { Bus } from '../../helpers/utils';

const { Content } = Layout;
const { Option } = Select;

const labelCol = {
  xs: 24,
  sm: 24,
  md: 8,
  lg: 8,
  xl: 6,
};

const labelColValue = {
  xs: 24,
  sm: 24,
  md: 16,
  lg: 16,
  xl: 12,
};

const keyTypes = ['secret', 'environment', 'settings', 'tag'];

const INIT_VALUE = { key_type: null };

export default function DeployAsSettings() {
  const store = React.useContext(storeContext);
  const [permission, setPermission] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [state, setState] = useState(INIT_VALUE);

  const columns = [
    {
      title: "ID",
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: "Device Type",
      dataIndex: 'device_type_name',
      key: 'device_type_id',
    },
    {
      title: "Serviço de aplicação",
      dataIndex: 'application_service_name',
      key: 'application_service_id',
    },
    {
      title: translate('default.keyType'),
      dataIndex: 'key_type',
      key: 'key_type',
    },
    {
      title: translate('default.key'),
      dataIndex: 'key',
      key: 'key',
    },
    {
      title: translate('default.value'),
      dataIndex: 'value',
      key: 'value',
      render: (text, record) => record.key_type === 'secret' && text ? '*********************' : text,
    },
    {
      title: '',
      align: 'right',
      key: 'action',
      width: 200,
      render: (_, record, index) => (
        <>
          <Button icon="edit" type="default" onClick={() => setState({ ...record, index })} htmlType="button" style={{ marginRight: 10 }} />
          <Button icon="delete" type="danger" onClick={() => Confirm({ func: () => remove(index) })} htmlType="button" />
        </>
      ),
    },
  ];

  function isDisabled() {
    return !state || !state.device_type_id || !state.application_service_id || !state.key || !state.key_type || !state.value;
  }

  async function remove(index) {
    try {
      setLoading(true);
      await api.delete(`/deploy_external_services_settings/${data[index].id}`);
      const temp = [...data];
      temp.splice(index, 1);
      setData(temp);
      Bus.emit("reloadDeployInfoDeviceTypes");
    } catch (error) {
      const authorized = isAuthorized(error);
      if (!authorized) {
        return setPermission(false);
      }
      store.error = { message: translate('default.errorOccurred') };
    } finally {
      setLoading(false);
    }
  }

  async function onAdd() {
    const model = {
      deploy_id: Number(store.currentItem.id),
      device_type_id: Number(state.device_type_id),
      application_service_id: Number(state.application_service_id),
      key_type: state.key_type,
      key: state.key,
      value: state.value,
    };
    await api.post('/deploy_external_services_settings', model);
    setState(INIT_VALUE);
    Bus.emit("reloadDeployInfoDeviceTypes");
  }

  async function onUpdate() {
    const model = {
      id: state.id,
      deploy_id: Number(store.currentItem.id),
      device_type_id: Number(state.device_type_id),
      application_service_id: Number(state.application_service_id),
      network_service_id: state.network_service_id ? Number(state.network_service_id) : null,
      key: state.key,
      key_type: state.key_type,
      value: state.value,
    };
    await api.put(`/deploy_external_services_settings/${state.id}`, model);
    setState(INIT_VALUE);
    Bus.emit("reloadDeployInfoDeviceTypes");
  }

  async function onSubmit(event) {
    setLoadingButton(true);
    event.preventDefault();
    setLoading(true);
    try {
      if (state.id) {
        await onUpdate();
      } else {
        await onAdd();
      }
      await list(false);
    } catch (error) {
      const authorized = isAuthorized(error);
      if (!authorized) {
        setPermission(false);
        return;
      }
      store.error = { message: translate('default.errorOccurred') };
    } finally {
      setLoading(false);
      setLoadingButton(false);
    }
  }

  const list = useCallback(async (shouldSetPermission = true) => {
    setLoading(true);
    try {
      const response = await api.get(`/deploy_external_services_settings/deploy/${store.currentItem.id}`);
      if (response.data && response.data.result) {
        return setData(response.data.result.filter(item => !!item.application_service_id));
      }
      setData([]);
    } catch (error) {
      const authorized = isAuthorized(error);
      if (!authorized && shouldSetPermission) {
        return setPermission(false);
      }
      if (!authorized && !shouldSetPermission) {
        return;
      }
      store.error = { message: translate('default.errorOccurred') };
    } finally {
      setLoading(false);
    }
  }, [store.currentItem.id, store.error]);

  useEffect(() => {
    list();
  }, [list]);

  return (
    <Content className="padding-20">
      {
        permission ?
          <>
            <Form layout="vertical" onSubmit={onSubmit}>
              <Row gutter={16}>
                <Col {...labelCol}>
                  <Form.Item label="Device Type" required>
                    <CustomAutoComplete
                      searchValue={'id'}
                      searchKey={'name'}
                      path={'/device_type?&active=1'}
                      placeholder={translate('default.select.deviceType')}
                      onSelect={(value) => setState(prevState => ({ ...prevState, device_type_id: value }))}
                      value={state.device_type_id}
                    />
                  </Form.Item>
                </Col>
                <Col {...labelCol}>
                  <Form.Item label="Serviço de aplicação" required>
                    <CustomAutoComplete
                      searchValue={'id'}
                      searchKey={'name'}
                      path={'/external_services?service_type=AS'}
                      placeholder={translate('default.select.name')}
                      onSelect={(value) => setState(prevState => ({ ...prevState, application_service_id: value }))}
                      value={state.application_service_id}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col {...labelCol}>
                  <Form.Item label={translate('default.key')} required>
                    <Input value={state.key}
                      onChange={(e) => {
                        const key = e.target.value;
                        setState(prevState => ({ ...prevState, key }));
                      }} />
                  </Form.Item>
                </Col>
                <Col {...labelCol}>
                  <Form.Item label={translate('default.keyType')} required>
                    <Select defaultValue={null}
                      value={state.key_type} style={{ width: '100%' }}
                      onChange={(key_type) => setState(prevState => ({ ...prevState, key_type }))} >
                      <Option key={-1} value={null}>{translate('default.select.keyType')}</Option>
                      {keyTypes.map(x => <Option key={x}>{x}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
                <Col {...labelColValue}>
                  <Form.Item label={translate('default.value')} required>
                    <Input value={state.value}
                      onChange={(e) => {
                        const value = e.target.value;
                        setState(prevState => ({ ...prevState, value }));
                      }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col>
                  <Form.Item>
                    <Button loading={loadingButton} type="primary" disabled={isDisabled()} htmlType="submit" >
                      {state.id ? translate('default.save') : translate('default.add')}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            <Col>
              <Table
                loading={loading}
                size="small"
                rowKey={record => record.id}
                columns={columns}
                dataSource={data}
                pagination={false}
              />
            </Col>
          </>
          :
          <AlertNotAllow />
      }
    </Content>
  );
}
